import type { AlertColor } from '@mui/material'
import type { Dispatch, SetStateAction } from 'react'
import { useState } from 'react'

import { articleApi } from '../services/articleApi'
import type { Article, ArticleState } from '../types/article.types'
import type { ArticleFormState, FormErrors } from '../types/article-form.types'
import { buildArticlePayload } from '../utils/articleFormUtils'
import { getFileSignature } from '../utils/fileUtils'
import { isRichTextEmpty, removeRichTextImageSources, replaceRichTextImageSources } from '../utils/richText'
import type { PendingInlineImage } from './useArticleUploads'
import type { ApiError } from '../../../shared/types/common'

type ShowSnackbar = (message: string, severity?: AlertColor) => void

type UseArticleSaveOptions = {
  clearPendingInlineImages: () => void
  clearPendingResources: () => void
  files: File[]
  form: ArticleFormState
  id?: string
  images: File[]
  isCustomerSpecific: boolean
  isEditing: boolean
  navigateHome: () => void
  pendingDeletedAttachmentIds: number[]
  pendingInlineImages: PendingInlineImage[]
  setArticleStatus: Dispatch<SetStateAction<ArticleState>>
  setForm: Dispatch<SetStateAction<ArticleFormState>>
  showSnackbar: ShowSnackbar
  uploadInlineImages: (articleId: string | number, pendingInlineImages: PendingInlineImage[]) => Promise<{
    sourceMap: Map<string, string>
    uploadedSignatures: Set<string>
  }>
  uploadRegularAttachments: (
    articleId: string | number,
    uploads: { file: File; attachmentType: 'image' | 'file' }[],
  ) => Promise<{ uploadedSignatures: Set<string> }>
}

type InlineUploadProgress = {
  sourceMap: Map<string, string>
  uploadedSignatures: Set<string>
}

type InlineUploadError = Error & Partial<InlineUploadProgress>
type RegularUploadError = Error & { uploadedSignatures?: Set<string> }

export function useArticleSave({
  clearPendingInlineImages,
  clearPendingResources,
  files,
  form,
  id,
  images,
  isCustomerSpecific,
  isEditing,
  navigateHome,
  pendingDeletedAttachmentIds,
  pendingInlineImages,
  setArticleStatus,
  setForm,
  showSnackbar,
  uploadInlineImages,
  uploadRegularAttachments,
}: UseArticleSaveOptions) {
  const [errors, setErrors] = useState<FormErrors>({})
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [recoveryArticleId, setRecoveryArticleId] = useState<string | number | null>(null)
  const [uploadedInlineSourceEntries, setUploadedInlineSourceEntries] = useState<[string, string][]>([])
  const [uploadedInlineSignatures, setUploadedInlineSignatures] = useState<string[]>([])
  const [uploadedRegularSignatures, setUploadedRegularSignatures] = useState<string[]>([])

  function validate() {
    const nextErrors: FormErrors = {}

    if (!form.title.trim()) nextErrors.title = 'Title is required'
    if (!form.discipline) nextErrors.discipline = 'Discipline is required'
    if (!form.technote_type) nextErrors.technote_type = 'Technote type is required'
    if (!form.scope) nextErrors.scope = 'Scope is required'
    if (isCustomerSpecific && !form.customer) nextErrors.customer = 'Customer is required'
    if (!form.confidentiality) nextErrors.confidentiality = 'Confidentiality is required'
    if (!form.brand) nextErrors.brand = 'Brand is required'
    if (!form.product_family.trim()) nextErrors.product_family = 'Product family is required'
    if (!form.asset_type) nextErrors.asset_type = 'Asset type is required'
    if (!form.technology_type) nextErrors.technology_type = 'Technology type is required'
    if (!form.summary.trim()) nextErrors.summary = 'Summary is required'
    if (isRichTextEmpty(form.problem)) nextErrors.problem = 'Problem / Background is required'
    if (isRichTextEmpty(form.solution)) nextErrors.solution = 'Solution / Procedure is required'

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  async function saveArticle(andSubmit = false) {
    if (!validate()) {
      setSubmitting(false)
      showSnackbar('Please fill in all required fields', 'error')
      return
    }

    setSaving(true)
    let createdArticleId: string | number | null = null
    try {
      const payload = buildArticlePayload(form, {
        isCustomerSpecific,
        pendingDeletedAttachmentIds,
      })

      let article: Article
      let savePayload = payload
      const rememberedInlineSourceMap = new Map(uploadedInlineSourceEntries)
      const uploadedInlineImageSignatures = new Set<string>(uploadedInlineSignatures)
      const uploadedRegularAttachmentSignatures = new Set<string>(uploadedRegularSignatures)
      const currentArticleId = id || recoveryArticleId

      const rememberInlineUploads = ({ sourceMap, uploadedSignatures }: InlineUploadProgress) => {
        sourceMap.forEach((source, temporarySource) => {
          rememberedInlineSourceMap.set(temporarySource, source)
        })
        uploadedSignatures.forEach((signature) => {
          uploadedInlineImageSignatures.add(signature)
        })
        setUploadedInlineSourceEntries(Array.from(rememberedInlineSourceMap.entries()))
        setUploadedInlineSignatures(Array.from(uploadedInlineImageSignatures))
      }

      const uploadMissingInlineImages = async (articleId: string | number) => {
        const missingInlineImages = pendingInlineImages.filter(
          (image) => !rememberedInlineSourceMap.has(image.temporarySource),
        )

        if (!missingInlineImages.length) {
          return
        }

        try {
          rememberInlineUploads(await uploadInlineImages(articleId, missingInlineImages))
        } catch (error) {
          const partialProgress = error as InlineUploadError
          if (partialProgress.sourceMap && partialProgress.uploadedSignatures) {
            rememberInlineUploads({
              sourceMap: partialProgress.sourceMap,
              uploadedSignatures: partialProgress.uploadedSignatures,
            })
          }

          throw error
        }
      }

      if (currentArticleId) {
        if (pendingInlineImages.length) {
          await uploadMissingInlineImages(currentArticleId)
          savePayload = {
            ...payload,
            problem: replaceRichTextImageSources(payload.problem, rememberedInlineSourceMap),
            solution: replaceRichTextImageSources(payload.solution, rememberedInlineSourceMap),
          }
        }

        const response = await articleApi.update(currentArticleId, savePayload)
        article = response.data
        setArticleStatus(response.data.state)
      } else {
        const pendingInlineSources = pendingInlineImages.map((image) => image.temporarySource)
        const createPayload = pendingInlineSources.length
          ? {
              ...payload,
              problem: removeRichTextImageSources(payload.problem, pendingInlineSources),
              solution: removeRichTextImageSources(payload.solution, pendingInlineSources),
            }
          : payload
        const response = await articleApi.create(createPayload)
        article = response.data
        createdArticleId = article.id
        setRecoveryArticleId(article.id)
        setArticleStatus(response.data.state)

        if (pendingInlineImages.length) {
          await uploadMissingInlineImages(article.id)
          savePayload = {
            ...payload,
            problem: replaceRichTextImageSources(payload.problem, rememberedInlineSourceMap),
            solution: replaceRichTextImageSources(payload.solution, rememberedInlineSourceMap),
          }

          const updateResponse = await articleApi.update(article.id, savePayload)
          article = updateResponse.data
        }
      }

      if (pendingInlineImages.length) {
        setForm((current) => ({
          ...current,
          problem: savePayload.problem,
          solution: savePayload.solution,
        }))
        clearPendingInlineImages()
      }

      const regularUploads = [
        ...images
          .filter((image) => {
            const signature = getFileSignature(image)
            return !uploadedInlineImageSignatures.has(signature) && !uploadedRegularAttachmentSignatures.has(signature)
          })
          .map((file) => ({ file, attachmentType: 'image' as const })),
        ...files
          .filter((file) => !uploadedRegularAttachmentSignatures.has(getFileSignature(file)))
          .map((file) => ({ file, attachmentType: 'file' as const })),
      ]

      try {
        const { uploadedSignatures } = await uploadRegularAttachments(article.id, regularUploads)
        uploadedSignatures.forEach((signature) => uploadedRegularAttachmentSignatures.add(signature))
        setUploadedRegularSignatures(Array.from(uploadedRegularAttachmentSignatures))
      } catch (error) {
        const partialProgress = error as RegularUploadError
        if (partialProgress.uploadedSignatures) {
          partialProgress.uploadedSignatures.forEach((signature) => uploadedRegularAttachmentSignatures.add(signature))
          setUploadedRegularSignatures(Array.from(uploadedRegularAttachmentSignatures))
        }

        throw error
      }

      if (andSubmit) {
        const submitResponse = await articleApi.submit(article.id)
        setArticleStatus(submitResponse.data.state)
        showSnackbar('Technote submitted for review')
      } else {
        showSnackbar(isEditing ? 'Technote updated' : 'Technote saved as draft')
      }

      clearPendingResources()
      setRecoveryArticleId(null)
      setUploadedInlineSourceEntries([])
      setUploadedInlineSignatures([])
      setUploadedRegularSignatures([])
      window.setTimeout(navigateHome, 1200)
    } catch (error) {
      const defaultMessage = createdArticleId || recoveryArticleId
        ? 'Draft was created. Fix the upload or submit error, then save again to continue this technote.'
        : 'Failed to save'
      showSnackbar((error as ApiError).response?.data?.message || defaultMessage, 'error')
    } finally {
      setSaving(false)
      setSubmitting(false)
    }
  }

  async function saveDraft() {
    await saveArticle(false)
  }

  async function submitForReview() {
    setSubmitting(true)
    await saveArticle(true)
  }

  return {
    errors,
    saveDraft,
    recoveryArticleId,
    saving,
    submitForReview,
    submitting,
  }
}
